import { FC, useState, useEffect } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet';
import { Block, Title, Text, ToggleIcon, ToggleText, Loading } from './Career.style';
import { SectionContent, PageType, SectionType } from '../../../types';
import { FormButton } from '../../styled';
import { IsVisible, Modal, SectionContainer } from '../../';
import { api } from '../../CMSAdmin/Api';

interface SectionParams {
  content: SectionContent[];
  offset?: number;
}

type MetaTagsProps = {
  careerTitle: string;
};

const TITLES = {
  ".NET": ".Net Developer",
  "React": "Front-End Developer /React/ ",
  "QA": "Automation QA ",
  "Internship": "Internship  "
};

const CareerMetaImage = {
  [TITLES['.NET']]: 'job-covers/dot-net-dev-fb-01.png',
  [TITLES['React']]: 'job-covers/front-end-dev-fb-01.png',
  [TITLES['QA']]: 'job-covers/aut-qa-dev-fb-01.png'
};

const MetaTags: FC<MetaTagsProps> = (props) => {
  const { careerTitle } = props;

  const imageUrl = CareerMetaImage[careerTitle];

  return (
    <Helmet>
      <meta property="og:title" content={`Apply for ${careerTitle}`} />
      <meta property="og:description" content={`Apply at DevOcean solutions for ${careerTitle}`} />
      { imageUrl ? <meta property="og:image" content={`${window.location.origin}/${imageUrl}`}/> : undefined }
    </Helmet>
  );
};

const CareerContent: FC<{
  text: string;
  title: string;
  index: number;
  isOpenOnInit?: boolean;
  offset?: number;
  setWantedJobRef: (data: string) => void;
}> = ({ text, title, index, isOpenOnInit = false, offset = 0, setWantedJobRef }) => {
  const [opened, setOpened] = useState(isOpenOnInit);

  useEffect(() => {
    setOpened(isOpenOnInit);
  }, [isOpenOnInit]);

  return (
    <IsVisible index={index} offset={offset} scrollIntoView={isOpenOnInit}>
      {
        isOpenOnInit && opened ? <MetaTags careerTitle={title} /> : undefined
      }
      <Title onClick={() => setOpened((currentState) => !currentState)}>
        {title}
        <ToggleIcon opened={opened}>✕</ToggleIcon>
      </Title>
      <ToggleText opened={opened}>
        <Text
          opened={opened}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(text, { allowedTags: false, allowedAttributes: false }) }}
        />
        <FormButton layout="primary" onClick={() => setWantedJobRef(title)}>
          Apply now &raquo;
        </FormButton>
      </ToggleText>
    </IsVisible>
  );
};

export const Career: FC<SectionParams> = ({ content, offset = 0 }) => {
  const [modelOpened, setModalOpened] = useState(false);
  const [wantedJobRef, setWantedJobRef] = useState('');
  const [applicationContent, setApplicationContent] = useState<PageType>();

  const { search } = useLocation();

  const [openPositionOnInit, setOpenPositionOnInit] = useState<string | null>('');

  useEffect(() => {
    const params = new URLSearchParams(search);
    const position = params.get("position");
    let title: string | null = null;
    if (position === ".NET") {
      title = TITLES[position];
    } else if (position === "React") {
      title = TITLES[position];
    } else if (position === "QA") {
      title = TITLES[position];
    } else if (position === "Internship") {
      title = TITLES[position];
    };
    setOpenPositionOnInit(title);
  }, [search]);


  useEffect(() => {
    if (!modelOpened) setWantedJobRef('');
  }, [modelOpened]);

  useEffect(() => {
    if (wantedJobRef) {
      api({ do: 'cms', action: 'getData', page: 'pages/application' }, (data) => {
        if (data.status === 'done') {
          setApplicationContent(data.data as PageType);
          setModalOpened(true);
        }
      });
    }
  }, [wantedJobRef]);

  return (
    <Block>
      {content.map((item, index) => {
        if (!item.text || !item.title) return null;

        return (
          <CareerContent
            index={index}
            offset={offset}
            key={String(index)}
            text={item.text}
            title={item.title}
            isOpenOnInit={item.title === openPositionOnInit}
            setWantedJobRef={setWantedJobRef}
          />
        );
      })}
      <Modal isOpen={modelOpened} setIsOpen={setModalOpened}>
        {!applicationContent ? (
          <Loading>Loading...</Loading>
        ) : (
          <SectionContainer
            sections={
              applicationContent.sections?.map((item) => {
                return {
                  ...item,
                  content: item.content?.map((row) => {
                    if (row.formFieldName === 'subject') row.formFieldDefaultValue = wantedJobRef;
                    return row;
                  }),
                };
              }) as SectionType[]
            }
          />
        )}
      </Modal>
    </Block>
  );
};
